import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Badge, Button, Card, HStack, Progress, Stack, Text,
} from '@chakra-ui/react'
import { useTranslation } from '../i18n'
import { toaster } from './ui/toaster'
import { VIDEO_AI_FEATURES } from '../constants/videoAI'
import { stopVideoAIBackfill } from '../services/videoAI'

// The export row's palette, for the same kind of thing: a background job.
const TONE = {
  running: 'blue', done: 'green', stopped: 'yellow', error: 'red',
}

const featureNames = (keys, t) => VIDEO_AI_FEATURES
  .filter((f) => (keys || []).includes(f.key))
  .map((f) => t(f.labelKey))
  .join(', ')

function JobBadge({ status }) {
  const { t } = useTranslation()
  return (
    <Badge colorPalette={TONE[status] || 'gray'}>
      {/* t() returns the key itself for an unknown status; TONE is the set we
          have copy for. */}
      {TONE[status] ? t(`videoAI.jobStatus.${status}`) : status}
    </Badge>
  )
}

function ProgressBar({ job }) {
  return (
    <Progress.Root value={job.done} max={job.total || 1} size="sm">
      <Progress.Track><Progress.Range /></Progress.Track>
    </Progress.Root>
  )
}

/**
 * The event's latest backfill job, in the AI settings tab: live progress and
 * Stop while it runs, the outcome once it has finished, until the next run
 * replaces it. Laid out like the export row in `videoExport/ExportList`.
 */
export function VideoAIBackfillCard({ eventId, job }) {
  const { t, lang } = useTranslation()
  const [stopping, setStopping] = useState(false)
  if (!job) return null
  const running = job.status === 'running'
  const startedAt = job.createdAt?.toDate?.().toLocaleString(lang)
  const finishedAt = job.finishedAt?.toDate?.().toLocaleString(lang)

  const stop = async () => {
    setStopping(true)
    try {
      await stopVideoAIBackfill(eventId)
      toaster.create({ title: t('videoAI.jobStopped'), type: 'info' })
    } catch (err) {
      toaster.create({ title: t('videoAI.jobStopFailed'), description: err.message, type: 'error' })
    } finally {
      setStopping(false)
    }
  }

  return (
    <Card.Root size="sm">
      <Card.Body>
        <Stack gap={2}>
          <HStack justify="space-between">
            <HStack gap={3}>
              <JobBadge status={job.status} />
              <Text fontSize="sm" color="fg.muted">
                {t('videoAI.jobAdds', { features: featureNames(job.features, t) })}
                {' · '}
                {t(job.applyModeration ? 'videoAI.jobModerationOn' : 'videoAI.jobModerationOff')}
              </Text>
            </HStack>
            {running && (
              <Button size="xs" variant="outline" loading={stopping} onClick={stop}>
                {t('videoAI.jobStop')}
              </Button>
            )}
          </HStack>

          {running && <ProgressBar job={job} />}
          <Text fontSize="xs" color="fg.muted">
            {t('videoAI.jobProgress', { done: job.done, total: job.total })}
            {job.failed > 0 && ` · ${t('videoAI.jobFailed', { count: job.failed })}`}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {t('videoAI.jobStartedBy', {
              name: job.createdBy?.name || job.createdBy?.email || '—',
              date: startedAt || '—',
            })}
            {!running && finishedAt && ` · ${t('videoAI.jobFinishedAt', { date: finishedAt })}`}
          </Text>

          {job.status === 'done' && job.failed > 0 && (
            <Text fontSize="xs" color="fg.muted">{t('videoAI.jobRetryHint')}</Text>
          )}
          {job.status === 'stopped' && (
            <Text fontSize="xs" color="fg.muted">{t('videoAI.jobStoppedHint')}</Text>
          )}
          {job.status === 'error' && job.error?.message && (
            <Text fontSize="xs" color="red.300">{job.error.message}</Text>
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}

/**
 * The same job on the event's Videos page, only while it runs, pointing back
 * to the settings tab where it can be stopped.
 */
export function VideoAIBackfillStrip({ eventId, job }) {
  const { t } = useTranslation()
  if (!job || job.status !== 'running') return null

  return (
    <Card.Root size="sm">
      <Card.Body>
        <Stack gap={2}>
          <HStack justify="space-between">
            <HStack gap={3}>
              <JobBadge status={job.status} />
              <Text fontSize="sm" color="fg.muted">
                {t('videoAI.jobStripTitle')}
                {' · '}
                {t('videoAI.jobProgress', { done: job.done, total: job.total })}
                {job.failed > 0 && ` · ${t('videoAI.jobFailed', { count: job.failed })}`}
              </Text>
            </HStack>
            <Button asChild size="xs" variant="outline">
              <RouterLink to={`/events/${eventId}/settings/ai`}>{t('videoAI.jobView')}</RouterLink>
            </Button>
          </HStack>
          <ProgressBar job={job} />
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}
